import * as Sentry from '@sentry/react'
import type { Breadcrumb, ErrorEvent } from '@sentry/react'
import { ApiError } from '@/api/client'

const dsn = import.meta.env.VITE_SENTRY_DSN

// One dist is rsynced to staging/demo/prod, so MODE isn't a useful
// environment tag at runtime — derive from the live hostname instead.
function deriveEnvironment(): string {
  if (typeof window === 'undefined') return 'unknown'
  const host = window.location.host
  if (host.startsWith('staging-') || host.startsWith('staging.')) return 'staging'
  if (host.startsWith('demo-') || host.startsWith('demo.')) return 'demo'
  if (host === 'localhost' || host.startsWith('localhost:') || host.startsWith('127.')) return 'development'
  return 'production'
}

/**
 * A URL without its query string or fragment (CLA-1694). Query strings can
 * carry tokens, and before CLA-1694 they carried what a clinician typed into a
 * patient search. Relative URLs (the fetch breadcrumbs' `/api/v1/...`) stay
 * relative.
 */
export function stripQuery(url: string): string {
  const cut = url.search(/[?#]/)
  return cut === -1 ? url : url.slice(0, cut)
}

const BREADCRUMB_URL_KEYS = ['url', 'from', 'to'] as const

/**
 * Fetch/XHR breadcrumbs carry the full request URL and navigation breadcrumbs
 * the page addresses, query strings included; `beforeSend` never saw them.
 */
export function scrubBreadcrumb(crumb: Breadcrumb): Breadcrumb {
  const data = crumb.data
  if (data) {
    for (const key of BREADCRUMB_URL_KEYS) {
      const v = data[key]
      if (typeof v === 'string') data[key] = stripQuery(v)
    }
  }
  if (typeof crumb.message === 'string' && /^(https?:\/\/|\/)/.test(crumb.message)) {
    crumb.message = stripQuery(crumb.message)
  }
  return crumb
}

/** Query params can carry tokens or PHI: drop them from the request and its Referer. */
export function scrubEvent<E extends ErrorEvent>(event: E): E {
  const req = event.request
  if (req) {
    if (req.url) req.url = stripQuery(req.url)
    delete req.query_string
    if (req.headers) {
      for (const name of Object.keys(req.headers)) {
        if (/^refer+er$/i.test(name)) req.headers[name] = stripQuery(req.headers[name])
      }
    }
  }
  event.breadcrumbs = event.breadcrumbs?.map(scrubBreadcrumb)
  return event
}

export function initSentry() {
  if (!dsn) return
  Sentry.init({
    dsn,
    environment: deriveEnvironment(),
    release: import.meta.env.VITE_RELEASE,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    beforeSend: (event) => scrubEvent(event),
    beforeBreadcrumb: (crumb) => scrubBreadcrumb(crumb),
  })
}

export function setSentryUser(user: { id: number | string; clinicId?: number | null } | null) {
  if (user === null) {
    Sentry.setUser(null)
    return
  }
  Sentry.setUser({
    id: String(user.id),
    ...(user.clinicId != null ? { segment: String(user.clinicId) } : {}),
  })
}

export function captureExceptionFromBoundary(error: Error, componentStack: string | null): string {
  return Sentry.captureException(error, {
    contexts: { react: { componentStack: componentStack ?? undefined } },
  })
}

/**
 * Whether a caught error is worth reporting to Sentry.
 *
 * React Query and React Router both *catch* errors before they can reach the
 * SDK's global handlers, so without an explicit capture they're invisible
 * (CLA-694). But not every caught error is a fault: expected HTTP outcomes
 * (auth, validation, permission, not-found) are user-driven and would drown
 * the real signal. We report server faults (5xx) and anything that isn't a
 * structured ApiError — raw network failures ("Failed to fetch") and genuine
 * JS bugs in fetcher/loader code.
 */
function isReportable(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 500
  return error instanceof Error
}

/**
 * Report an error that a framework caught before it could reach the SDK's
 * global handlers — React Query query/mutation failures and React Router
 * loader/render errors. No-ops for expected HTTP noise (see isReportable).
 */
export function captureHandledError(
  error: unknown,
  context: { source: 'react-query' | 'react-router'; [key: string]: unknown },
): void {
  if (!isReportable(error)) return
  const { source, ...rest } = context
  Sentry.captureException(error, {
    tags: { handled_source: source },
    ...(Object.keys(rest).length > 0 ? { contexts: { claro: rest } } : {}),
  })
}

/**
 * A booking whose clinic time zone could not be confirmed (CLA-1519).
 *
 * The booking modal pauses rather than guess the zone (a guess books the
 * patient at the wrong hour), so every occurrence is a clinician who could not
 * book. Reported as one stable message, fingerprinted to one issue, with the
 * clinic as a tag so it is countable and alertable per clinic. Unlike
 * `captureHandledError` this reports 4xx too: a 403 here still stops a booking.
 * Only the clinic id and the failure go in — nothing about the patient.
 */
export function captureBookingTzUnresolved(detail: {
  targetClinicId: number | null
  status: number | null
  error: string
}): void {
  Sentry.captureMessage('booking_tz_unresolved', {
    level: 'error',
    fingerprint: ['booking_tz_unresolved'],
    tags: {
      booking_tz: 'unresolved',
      target_clinic_id: detail.targetClinicId == null ? 'unknown' : String(detail.targetClinicId),
    },
    contexts: {
      claro: {
        target_clinic_id: detail.targetClinicId,
        status: detail.status,
        error: detail.error.slice(0, 200),
      },
    },
  })
}
